import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import type { VehicleBag } from '../types';
import { STOCK_CAR_SEARCH, STOCK_CAR_BY_ID } from '../schemas/oneGraphSchema';
import type {
  StockCarType,
  StockCarSearchResponse,
  StockCarResponse,
} from '../types/oneGraphTypes';

type StockCarInputs = {
  vehicleId: string | null | undefined;
  market: string;
  isTier3: boolean;
  isMock: boolean;
};

type UseStockCar = (props: StockCarInputs) => VehicleBag;

const useStockCar: UseStockCar = ({ vehicleId, market, isTier3, isMock }) => {
  const isStockCarId = !!vehicleId?.length && vehicleId.length > 17 && vehicleId.endsWith('=');
  const importDb = useMemo(() => {
    const isUsMarket = market?.includes('us');
    const isUsedVehicles = market?.includes('uc');

    if (isMock) {
      if (isUsMarket) return 'AGC_USA_NARWHAL'; // US Mock Vehicles crated by NARWHAL team
      return 'AGC_CAN_NARWHAL'; // CANADA Mock Vehicles crated by NARWHAL team
    }
    return isUsMarket ? `AGC_USA_JDP` : `AGC_CAN_JDP`;
  }, [market, isMock]);

  const importFilter = useMemo(() => {
    return {
      id: 'stat-import',
      items: [importDb],
    };
  }, [importDb]);

  const commonMarketIdentifier = useMemo(() => {
    if (!market) return null;
    return {
      brand: 'A',
      country: market.includes('ca') ? 'ca' : 'us',
      language: 'en',
    };
  }, [market]);

  const vinQueryVariables = useMemo(() => {
    if (!vehicleId || !commonMarketIdentifier) return null;
    return {
      stockIdentifier: {
        marketIdentifier: commonMarketIdentifier,
        stockCarsType: market?.includes('uc') ? 'USED' : 'NEW',
      },
      searchParameter: {
        paging: { limit: 2, offset: 0 },
        criteria: [
          importFilter, // replace with real importFilter logic
          { id: 't_vin', items: [vehicleId] },
        ],
      },
    };
  }, [vehicleId, commonMarketIdentifier, market, importFilter]);

  const idQueryVariables = useMemo(() => {
    if (!vehicleId || !commonMarketIdentifier) return null;
    return {
      stockCarIdentifier: {
        id: vehicleId,
        stockIdentifier: {
          marketIdentifier: commonMarketIdentifier,
          stockCarsType: market?.includes('uc') ? 'USED' : 'NEW',
        },
      },
    };
  }, [vehicleId, commonMarketIdentifier, market]);

  const {
    data: vinData,
    loading: vinLoading,
    error: vinError,
  } = useQuery<StockCarSearchResponse>(STOCK_CAR_SEARCH, {
    variables: vinQueryVariables ?? undefined,
    skip: !vehicleId || isStockCarId || !vinQueryVariables,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  const {
    data: idData,
    loading: idLoading,
    error: idError,
  } = useQuery<StockCarResponse>(STOCK_CAR_BY_ID, {
    variables: idQueryVariables ?? undefined,
    skip: !vehicleId || !isStockCarId || !idQueryVariables,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  const stockCar: StockCarType | null = useMemo(() => {
    if (!vehicleId) return null;
    // VIN Search Data
    if (!isStockCarId && vinData?.stockCarSearch) {
      const { resultNumber, results } = vinData.stockCarSearch;
      return resultNumber > 0 && resultNumber < 5 ? (results.cars[0]?.stockCar ?? null) : null;
    }
    // ID search data
    if (isStockCarId && idData?.stockCar) {
      return idData.stockCar;
    }
    return null;
  }, [vehicleId, isStockCarId, vinData, idData]);

  return {
    vehicle: stockCar,
    loading: vinLoading || idLoading,
    error: vinError || idError,
    isTier3,
    market,
  };
};

export default useStockCar;
